import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RoleDetail } from '../signing-page/signing-page.dto';
import { EsignAndSmsPinService } from './esign-and-sms-pin-service';
import { GlobalService } from 'app/global/global.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-esign-and-sms-pin',
  templateUrl: './esign-and-sms-pin.component.html',
  styleUrl: './esign-and-sms-pin.component.scss'
})
export class EsignAndSmsPinComponent {
  @Input() roleDetail: RoleDetail;
  @Output() signDocument = new EventEmitter<null>();
  constructor(private esignAndSmsPinService:EsignAndSmsPinService, private globalService: GlobalService, private fb: FormBuilder) { }
  public sendSmsPin: boolean = true; // Flag to control the visibility of the send SMS pin button
  public sendSmsPinLoading: boolean = false; // Flag to control the loading state of the send SMS pin button
  public pinForm!: FormGroup; // Form group for the pin input field
  public pinFormLoading: boolean = false; // Flag to control the loading state of the pin form
  public pinFormSubmitted: boolean = false; // Flag to control the submission state of the pin form
  private pinVerifiedSuccess: boolean = false; // Flag to indicate if the pin was verified successfully
  
  /**
   * Sends an SMS PIN to the recipient's phone number associated with the current role.
   * 
   * This method sets a loading state while the SMS PIN is being sent and handles both
   * success and error scenarios. On success, it resets the loading state, disables the
   * send PIN button, and reinitializes the form. On error, it resets the loading state
   * and displays an error message using the global service.
   * 
   * @remarks
   * - The `sendSmsPinLoading` property is used to indicate the loading state.
   * - The `sendSmsPin` property is used to control the visibility of the send PIN button.
   * - The `initForm` method is called to reinitialize the form after successfully sending the PIN.
   * - Errors are handled by displaying a toast notification using the global service.
   * 
   * @throws Will display an error toast if the SMS PIN sending fails.
   */
  sendPin() {
    this.sendSmsPinLoading = true; // Set loading state to true
    this.esignAndSmsPinService.sendSmsPin(this.roleDetail.id, this.roleDetail?.recipient?.phone_no).subscribe({
      next: () => {
        this.sendSmsPinLoading = false; // Reset loading state
        this.sendSmsPin = false;
        this.initForm(); // Initialize the form after sending the SMS pin
      },
      error: (error) => {
        this.sendSmsPinLoading = false; // Reset loading state
        this.globalService.toastrError(error);
      },
    })
  }

  /**
   * Initializes the form group for the component.
   * Creates a reactive form with a single control `pin` that is required.
   * This form is used to handle user input for the PIN field.
   */
  initForm(): void {
    this.pinForm = this.fb.group({
      pin: ['', Validators.required],
    });
  }

  /**
   * Verifies the PIN entered by the user.
   * 
   * This method performs the following steps:
   * 1. Marks the form as submitted.
   * 2. Checks if the form is invalid and exits early if so.
   * 3. If the PIN has already been successfully verified, emits the sign document event and exits.
   * 4. Sets the loading state and resets the submission state.
   * 5. Calls the `verifyPin` service method to validate the PIN.
   * 
   * On successful PIN verification:
   * - Resets the loading state.
   * - Marks the PIN as successfully verified.
   * - Disables the PIN input field.
   * - Emits the sign document event.
   * 
   * On error:
   * - Displays an error message using the global service.
   * - Resets the loading state.
   */
  verifyPin(){
    this.pinFormSubmitted = true; // Set form submission state to true
    if(this.pinForm.invalid){
      return; // If the form is invalid, do not proceed with verification
    }
    if(this.pinVerifiedSuccess){
      this.signDocument.emit(null); // Emit the sign document event
      return; // If the pin was already verified successfully, do not proceed again
    }
    this.pinFormLoading = true; // Set loading state to true
    this.pinFormSubmitted = false; // Reset form submission state
    this.esignAndSmsPinService.verifyPin(this.roleDetail.id, this.pinForm.value).subscribe({
      next: () => {
        this.pinFormLoading = false; // Reset loading state
        this.pinVerifiedSuccess = true; // Set pin verified success state to true
        this.pinForm.get('pin')?.disable(); // Disable the pin input field
        this.signDocument.emit(null); // Emit the sign document event
      },
      error: (error) => {
        this.globalService.toastrError(error); // Show error message
        this.pinFormLoading = false; // Reset loading state
        this.pinVerifiedSuccess = false; // Reset pin verified success state
      },
    });
  }

  /**
   * Navigates back to the SMS PIN sending step.
   * This method sets the `sendSmsPin` flag to `true` to indicate
   * that the SMS PIN sending view should be displayed, and resets
   * the `pinForm` to clear any previously entered data.
   */
  backToTheSendSms(){
    this.sendSmsPin = true;
    this.pinVerifiedSuccess = false; // Reset pin verified success state
    this.pinForm.reset(); // Reset the form
  }
}
